// Safe Exam Browser (SEB) JavaScript API integration.
//
// Hintergrund: SEB fügt den "X-SafeExamBrowser-ConfigKeyHash"-HTTP-Header nur bei Requests
// hinzu, deren Ziel-Host mit dem Host der aktuell geladenen Seite übereinstimmt (siehe
// SEB-Quellcode ResourceHandler.AppendCustomHeaders). Da unser Frontend (ki.keensight.ch)
// und unsere API (kiapi.keensight.ch) auf unterschiedlichen Hosts laufen, wird der Header
// für API-Requests nie mitgeschickt. Deshalb lesen wir den Config Key stattdessen über die
// offizielle SEB JavaScript API direkt von der Seite aus und senden ihn selbst als
// Custom-Header mit unseren API-Requests.
//
// Doku: https://safeexambrowser.org/developer/seb-config-key.html (Abschnitt
// "Query the Config Key and Browser Exam Key with the SEB JavaScript API")

interface SafeExamBrowserSecurity {
  configKey?: string;
  browserExamKey?: string;
  updateKeys?: (callback: () => void) => void;
}

interface SafeExamBrowserGlobal {
  security?: SafeExamBrowserSecurity;
  version?: string;
}

declare global {
  interface Window {
    SafeExamBrowser?: SafeExamBrowserGlobal;
  }
}

export interface SebKeys {
  configKey: string;
  browserExamKey?: string;
  // Die Seiten-URL, mit der SEB den configKey/browserExamKey verknüpft hat (ohne Fragment).
  // Wird unverändert an die API weitergegeben, damit das Backend denselben Hash nachrechnen kann.
  pageUrl: string;
}

// Zeitlimit, falls SafeExamBrowser.security.updateKeys() aus irgendeinem Grund nie zurückkehrt.
const UPDATE_KEYS_TIMEOUT_MS = 3000;

// WICHTIG: SEB berechnet Config-Key und Browser-Exam-Key einmalig beim Erstellen des
// Render-Prozesses (OnContextCreated) aus der URL, die zum Zeitpunkt des Ladens im Frame
// aktiv ist. Bei einer SPA-Navigation (history.pushState / React Router) ruft SEB
// OnContextCreated NICHT erneut auf, und die Schlüssel bleiben für die ursprüngliche
// Lade-URL gültig. Deshalb darf pageUrl nicht aus dem aktuellen window.location.href
// gelesen werden, sondern muss die URL beim initialen Seitenladen verwenden.
const initialPageUrl = window.location.href.split('#')[0];

let cachedSebKeysPromise: Promise<SebKeys | null> | null = null;

// Ermittelt configKey/browserExamKey über die SEB JS API. Gibt null zurück, wenn die App
// nicht innerhalb von SEB läuft. Das Ergebnis wird für die gesamte Session zwischengespeichert:
// SEB berechnet die Keys einmalig für die zu diesem Zeitpunkt geladene Seiten-URL und
// aktualisiert sie bei Client-seitiger SPA-Navigation nicht erneut.
export function getSebKeys(): Promise<SebKeys | null> {
  if (!cachedSebKeysPromise) {
    cachedSebKeysPromise = resolveSebKeys();
  }
  return cachedSebKeysPromise;
}

async function resolveSebKeys(): Promise<SebKeys | null> {
  // SEB injiziert window.SafeExamBrowser im OnContextCreated-Handler, der (je nach Timing)
  // kurz vor oder nach dem App-Start ausgeführt wird. Falls die Variable noch nicht
  // verfügbar ist, warten wir kurz, bevor wir aufgeben.
  let seb = window.SafeExamBrowser;

  if (!seb?.security) {
    await new Promise((resolve) => setTimeout(resolve, 500));
    seb = window.SafeExamBrowser;

    if (!seb?.security) {
      return null; // Kein SEB-Client (normaler Browser)
    }
  }

  // SEB berechnet die Keys aus der URL, die beim initialen Laden des Frames aktiv war.
  // Siehe Kommentar bei initialPageUrl.
  const pageUrl = initialPageUrl;

  // Ab SEB 3.3.2 (Windows) / 3.1 (macOS/iOS) sind die Variablen bereits beim Laden gesetzt.
  if (seb.security.configKey) {
    return { configKey: seb.security.configKey, browserExamKey: seb.security.browserExamKey, pageUrl };
  }

  // Ältere SEB-Versionen benötigen den expliziten Aufruf von updateKeys().
  if (typeof seb.security.updateKeys === 'function') {
    await new Promise<void>((resolve) => {
      let resolved = false;
      const finish = () => {
        if (!resolved) {
          resolved = true;
          resolve();
        }
      };
      seb.security!.updateKeys!(finish);
      setTimeout(finish, UPDATE_KEYS_TIMEOUT_MS);
    });

    if (seb.security.configKey) {
      return { configKey: seb.security.configKey, browserExamKey: seb.security.browserExamKey, pageUrl };
    }
  }

  console.warn('SEB erkannt, aber configKey konnte nicht über die SEB JavaScript API ermittelt werden.');
  return null;
}
